import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  OnInit,
  Output,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  EstabelecimentoDataService,
  EstabelecimentoService,
  ProfissionalDataService,
  ProfissionalService,
  ServicoDataService,
} from '@nomoh-monorepo/data-access-bardo-administrador';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import {
  DeviceService,
  FormFieldComponent,
  NMultiselectComponent,
} from '@nomoh-monorepo/shared-common-ui';
import {
  DiasSemanaOptions,
  IEstabelecimento,
  IProfissional,
  IProfissionalDiasPresente,
  IProfissionalServico,
} from '@nomoh-monorepo/shared-types';
import {
  expedienteObrigatorio,
  prestaAtendimentoEditavel,
  prestaAtendimentoSugerido,
  servicosObrigatorios,
} from '@nomoh-monorepo/bardo-administrador-util';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { DatePickerModule } from 'primeng/datepicker';
import { InputMaskModule } from 'primeng/inputmask';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { ToggleButtonModule } from 'primeng/togglebutton';

@Component({
  selector: 'lib-etapa-profissionais',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    InputMaskModule,
    MultiSelectModule,
    SelectModule,
    DatePickerModule,
    MessageModule,
    ChipModule,
    ToggleButtonModule,
    FormFieldComponent,
    NMultiselectComponent,
    TranslateModule,
  ],
  templateUrl: './etapa-profissionais.component.html',
  styleUrls: ['./etapa-profissionais.component.scss'],
})
export class EtapaProfissionaisComponent implements OnInit, AfterViewInit {
  @ViewChild('headerTemplate')
  private headerTemplateRef?: TemplateRef<unknown>;
  @ViewChild('footerTemplate')
  private footerTemplateRef?: TemplateRef<unknown>;

  @Output() proximaEtapa = new EventEmitter<void>();
  @Output() etapaAnterior = new EventEmitter<void>();
  @Output() fechar = new EventEmitter<void>();
  @Output() headerTemplateAction = new EventEmitter<TemplateRef<unknown>>();
  @Output() footerTemplateAction = new EventEmitter<TemplateRef<unknown>>();

  private fb = inject(FormBuilder);
  private profissionalService = inject(ProfissionalService);
  private profissionalDataService = inject(ProfissionalDataService);
  private servicoDataService = inject(ServicoDataService);
  private estabelecimentoDataService = inject(EstabelecimentoDataService);
  private estabelecimentoService = inject(EstabelecimentoService);
  private notificacaoService = inject(NotificacaoService);
  private translateService = inject(TranslateService);
  readonly deviceService = inject(DeviceService);

  form: FormGroup;
  profissionaisSignal = signal<Partial<IProfissional>[]>([]);
  $loading = signal(false);
  estabelecimento: IEstabelecimento = {} as IEstabelecimento;

  // Signals dos DataServices
  $servicos = this.servicoDataService.$servicos;
  $estabelecimentoAtual = this.estabelecimentoDataService.$estabelecimentoAtual;

  $atingiuLimiteProfissionais = computed(() => {
    const max = this.estabelecimentoDataService.$planoQuotas()?.maxProfissionais;
    if (max == null) return false;
    const novos = this.profissionaisSignal().filter(
      (p) => p.perfilAcesso === 'PROFISSIONAL' || !p.perfilAcesso
    ).length;
    return this.profissionalDataService.$profissionaisNaQuota() + novos >= max;
  });

  $mensagemLimiteProfissionais = computed(() => {
    const quotas = this.estabelecimentoDataService.$planoQuotas();
    if (!quotas?.maxProfissionais) return '';
    return this.translateService.instant('PRIMEIRO_ACESSO.PROFISSIONAIS.MENSAGEM_LIMITE', {
      plano: quotas.nome ?? '',
      max: quotas.maxProfissionais,
    });
  });

  perfisAcesso: { label: string; value: string }[] = [];

  // Opções de dias da semana
  diasSemanaOptions: { id: number; label: string }[] = [];

  // Opções de horários (geradas dinamicamente)
  horariosInicio: { label: string; value: string }[] = [];
  horariosFim: { label: string; value: string }[] = [];
  horariosAlmocoInicio: { label: string; value: string }[] = [];
  horariosAlmocoFim: { label: string; value: string }[] = [];

  /**
   * Serviços/expediente são obrigatórios para PROFISSIONAL/ADMINISTRADOR;
   * opcionais para RECEPCAO sem serviço vinculado (aprovado em 2026-07-10 —
   * docs/perfil/REFINAMENTO_SERVICOS_OPCIONAIS_RECEPCAO.md). Mesma regra do
   * profissional-create-edit — mantida aqui para não reintroduzir o bug no
   * onboarding (Primeiro Acesso).
   */
  $servicosObrigatorios = signal(true);
  $expedienteObrigatorio = signal(true);
  $servicosHelpText = signal('');
  $expedienteHelpText = signal('');

  constructor() {
    this.form = this.fb.group({
      nome: [
        '',
        [Validators.required, Validators.minLength(3), Validators.maxLength(50)],
      ],
      descricao: ['', [Validators.maxLength(100)]],
      perfilAcesso: ['', Validators.required],
      prestaAtendimento: [true],
      diasSelecionados: [[]],
      servicosSelecionados: [[]],
      inicioExpediente: [''],
      fimExpediente: [{ value: '', disabled: true }],
      almocoInicio: [{ value: '', disabled: true }],
      almocoFim: [{ value: '', disabled: true }],
    });

    this.atualizarLabels();

    effect(() => {
      this.$estabelecimentoAtual();
      this.atualizarLabels();
    });
  }

  private obterChaveDia(id: number): string {
    const mapa: Record<number, string> = {
      1: 'BARDO_ESTABELECIMENTOS.DIAS_SEMANA.DOMINGO',
      2: 'BARDO_ESTABELECIMENTOS.DIAS_SEMANA.SEGUNDA',
      3: 'BARDO_ESTABELECIMENTOS.DIAS_SEMANA.TERCA',
      4: 'BARDO_ESTABELECIMENTOS.DIAS_SEMANA.QUARTA',
      5: 'BARDO_ESTABELECIMENTOS.DIAS_SEMANA.QUINTA',
      6: 'BARDO_ESTABELECIMENTOS.DIAS_SEMANA.SEXTA',
      7: 'BARDO_ESTABELECIMENTOS.DIAS_SEMANA.SABADO',
    };
    return mapa[id] || '';
  }

  private atualizarLabels(): void {
    this.perfisAcesso = [
      { label: this.translateService.instant('BARDO_PROFISSIONAIS.PERFIS.ADMINISTRADOR'), value: 'ADMINISTRADOR' },
      { label: this.translateService.instant('BARDO_PROFISSIONAIS.PERFIS.RECEPCAO'), value: 'RECEPCAO' },
      { label: this.translateService.instant('BARDO_PROFISSIONAIS.PERFIS.PROFISSIONAL'), value: 'PROFISSIONAL' },
    ];
    this.diasSemanaOptions = DiasSemanaOptions.map((dia) => {
      const chaveDia = this.obterChaveDia(dia.id);
      return {
        id: dia.id,
        label: chaveDia ? this.translateService.instant(chaveDia) : dia.nome,
      };
    }).filter((dia) =>
      this.$estabelecimentoAtual()?.diasFuncionamento?.some(
        (d) => Number(d.dia?.id) === dia.id
      )
    );
  }

  ngOnInit() {
    this.translateService.onLangChange.subscribe(() => {
      this.atualizarLabels();
      this.atualizarValidadoresPorPerfil();
    });
    this.gerarHorariosDisponiveis();
    void this.estabelecimentoService.getPlanoQuotas();
    const estabelecimentoId = this.$estabelecimentoAtual()?.id;
    if (estabelecimentoId) {
      void this.profissionalService.findAll({ estabelecimentoId });
    }
    this.form.get('perfilAcesso')?.valueChanges.subscribe(() => {
      this.atualizarControlePrestaAtendimento();
      this.atualizarValidadoresPorPerfil();
    });
    this.form.get('prestaAtendimento')?.valueChanges.subscribe(() => {
      this.atualizarValidadoresPorPerfil();
    });
    this.form.get('servicosSelecionados')?.valueChanges.subscribe(() => {
      this.atualizarValidadoresPorPerfil();
    });
    this.atualizarControlePrestaAtendimento();
    this.atualizarValidadoresPorPerfil();
    this.form.get('inicioExpediente')?.valueChanges.subscribe((inicio) => {
      this.gerarHorariosFim(inicio);
      const fimExpedienteCtrl = this.form.get('fimExpediente');
      if (inicio) {
        fimExpedienteCtrl?.enable({ emitEvent: false });
      } else {
        fimExpedienteCtrl?.disable({ emitEvent: false });
      }
    });
    this.form.get('fimExpediente')?.valueChanges.subscribe(() => {
      this.gerarHorariosAlmocoInicio();
      const almocoInicio = this.form.get('almocoInicio')?.value;
      if (almocoInicio) {
        this.gerarHorariosAlmocoFim(almocoInicio);
      }
    });
    this.form.get('almocoInicio')?.valueChanges.subscribe((inicio) => {
      this.gerarHorariosAlmocoFim(inicio);
      const almocoFimCtrl = this.form.get('almocoFim');
      if (inicio) {
        almocoFimCtrl?.enable({ emitEvent: false });
      } else {
        almocoFimCtrl?.disable({ emitEvent: false });
      }
    });

    this.gerarHorariosAlmocoInicio();
  }

  ngAfterViewInit(): void {
    if (this.headerTemplateRef) {
      this.headerTemplateAction.emit(this.headerTemplateRef);
    }
    if (this.footerTemplateRef) {
      this.footerTemplateAction.emit(this.footerTemplateRef);
    }
  }

  gerarHorariosDisponiveis() {
    const estabelecimento = this.$estabelecimentoAtual();
    if (!estabelecimento?.horaAbertura || !estabelecimento?.horaFechamento) {
      return;
    }

    const inicio = this.parseHorario(estabelecimento.horaAbertura);
    const fim = this.parseHorario(estabelecimento.horaFechamento);

    this.horariosInicio = [];
    let current = inicio;

    while (current < fim) {
      const horarioStr = this.formatarHorario(current);
      this.horariosInicio.push({ label: horarioStr, value: horarioStr });
      current += 30; // Incremento de 30 minutos
    }

    this.horariosFim = [];
    this.horariosAlmocoInicio = [];
    this.horariosAlmocoFim = [];
  }

  gerarHorariosAlmocoInicio() {
    const inicioExpediente = this.form.get('inicioExpediente')?.value;
    const fimExpediente = this.form.get('fimExpediente')?.value;

    if (!inicioExpediente || !fimExpediente) {
      this.horariosAlmocoInicio = [];
      this.form.patchValue(
        { almocoInicio: '', almocoFim: '' },
        { emitEvent: false }
      );
      this.form.get('almocoInicio')?.disable({ emitEvent: false });
      this.form.get('almocoFim')?.disable({ emitEvent: false });
      this.horariosAlmocoFim = [];
      return;
    }

    const inicio = this.parseHorario(inicioExpediente);
    const fim = this.parseHorario(fimExpediente);

    this.horariosAlmocoInicio = [];
    let current = inicio;

    while (current <= fim - 30) {
      const horarioStr = this.formatarHorario(current);
      this.horariosAlmocoInicio.push({ label: horarioStr, value: horarioStr });
      current += 30;
    }

    const almocoInicioAtual = this.form.get('almocoInicio')?.value;
    if (
      almocoInicioAtual &&
      !this.horariosAlmocoInicio.some((h) => h.value === almocoInicioAtual)
    ) {
      this.form.patchValue(
        { almocoInicio: '', almocoFim: '' },
        { emitEvent: false }
      );
      this.horariosAlmocoFim = [];
      this.form.get('almocoFim')?.disable({ emitEvent: false });
    }

    if (this.horariosAlmocoInicio.length > 0) {
      this.form.get('almocoInicio')?.enable({ emitEvent: false });
    } else {
      this.form.get('almocoInicio')?.disable({ emitEvent: false });
    }
  }

  gerarHorariosFim(inicioSelecionado: string) {
    if (!inicioSelecionado) {
      this.horariosFim = [];
      return;
    }

    const estabelecimento = this.$estabelecimentoAtual();
    if (!estabelecimento?.horaFechamento) return;

    const inicio = this.parseHorario(inicioSelecionado);
    const fim = this.parseHorario(estabelecimento.horaFechamento);

    this.horariosFim = [];
    let current = inicio + 30; // Mínimo 30min após início

    while (current <= fim) {
      const horarioStr = this.formatarHorario(current);
      this.horariosFim.push({ label: horarioStr, value: horarioStr });
      current += 30;
    }

    // Reset fim se inválido
    const fimAtual = this.form.get('fimExpediente')?.value;
    if (fimAtual && this.parseHorario(fimAtual) <= inicio) {
      this.form.patchValue({ fimExpediente: '' });
    }

    if (this.horariosFim.length > 0) {
      this.form.get('fimExpediente')?.enable();
    } else {
      this.form.get('fimExpediente')?.disable();
      this.form.patchValue({ fimExpediente: '' });
    }

    this.gerarHorariosAlmocoInicio();

    const almocoInicio = this.form.get('almocoInicio')?.value;
    if (almocoInicio) {
      this.gerarHorariosAlmocoFim(almocoInicio);
    }
  }

  gerarHorariosAlmocoFim(inicioSelecionado: string) {
    if (!inicioSelecionado) {
      this.horariosAlmocoFim = [];
      this.form.patchValue({ almocoFim: '' }, { emitEvent: false });
      this.form.get('almocoFim')?.disable({ emitEvent: false });
      return;
    }

    const fimExpediente = this.form.get('fimExpediente')?.value;
    if (!fimExpediente) {
      this.horariosAlmocoFim = [];
      return;
    }

    const inicio = this.parseHorario(inicioSelecionado);
    const fim = this.parseHorario(fimExpediente);

    this.horariosAlmocoFim = [];
    let current = inicio + 30;

    while (current <= fim) {
      const horarioStr = this.formatarHorario(current);
      this.horariosAlmocoFim.push({ label: horarioStr, value: horarioStr });
      current += 30;
    }

    const fimAtual = this.form.get('almocoFim')?.value;
    if (fimAtual && this.parseHorario(fimAtual) <= inicio) {
      this.form.patchValue({ almocoFim: '' });
    }

    if (this.horariosAlmocoFim.length > 0) {
      this.form.get('almocoFim')?.enable();
    } else {
      this.form.get('almocoFim')?.disable();
      this.form.patchValue({ almocoFim: '' });
    }
  }

  /**
   * PROFISSIONAL: trava o controle em true (decisão #1, 2026-07-16). Demais
   * perfis: libera o controle e, enquanto o usuário não tiver mexido
   * manualmente no toggle (control.pristine), aplica a sugestão de valor por
   * perfil (decisão #2). Este componente só cria profissionais novos
   * (onboarding), sem modo edição — não precisa checar registro existente.
   */
  private atualizarControlePrestaAtendimento(): void {
    const perfilAcesso = this.form.get('perfilAcesso')?.value;
    const control = this.form.get('prestaAtendimento');

    if (!prestaAtendimentoEditavel(perfilAcesso)) {
      control?.setValue(true, { emitEvent: false });
      control?.disable({ emitEvent: false });
      return;
    }

    control?.enable({ emitEvent: false });
    if (control?.pristine) {
      control.setValue(prestaAtendimentoSugerido(perfilAcesso), {
        emitEvent: false,
      });
    }
  }

  /**
   * Reaplica os validadores de servicosSelecionados/expediente conforme
   * prestaAtendimento e os serviços selecionados no momento (Opção B —
   * docs/perfil/PROPOSTA_FLAG_PRESTA_ATENDIMENTO.md — mesma regra do
   * profissional-create-edit, mantida aqui para não reintroduzir o bug no
   * onboarding).
   */
  private atualizarValidadoresPorPerfil(): void {
    const prestaAtendimento = this.form.get('prestaAtendimento')?.value;
    const servicosSelecionados = this.form.get('servicosSelecionados')?.value;

    const precisaServicos = servicosObrigatorios(prestaAtendimento);
    this.$servicosObrigatorios.set(precisaServicos);
    this.aplicarValidadorRequired('servicosSelecionados', precisaServicos);

    const precisaExpediente = expedienteObrigatorio(
      prestaAtendimento,
      servicosSelecionados
    );
    this.$expedienteObrigatorio.set(precisaExpediente);
    this.aplicarValidadorRequired('diasSelecionados', precisaExpediente);
    this.aplicarValidadorRequired('inicioExpediente', precisaExpediente);
    this.aplicarValidadorRequired('fimExpediente', precisaExpediente);

    if (precisaServicos) {
      this.$servicosHelpText.set('');
    } else if (servicosSelecionados?.length) {
      this.$servicosHelpText.set(
        this.translateService.instant('PRIMEIRO_ACESSO.PROFISSIONAIS.SERVICOS_HELP_COM_SERVICOS')
      );
    } else {
      this.$servicosHelpText.set(
        this.translateService.instant('PRIMEIRO_ACESSO.PROFISSIONAIS.SERVICOS_HELP_SEM_SERVICOS')
      );
    }

    this.$expedienteHelpText.set(
      precisaExpediente ? '' : this.translateService.instant('PRIMEIRO_ACESSO.PROFISSIONAIS.EXPEDIENTE_HELP_RECEPCAO')
    );
  }

  private aplicarValidadorRequired(controlName: string, required: boolean): void {
    const control = this.form.get(controlName);
    control?.setValidators(required ? Validators.required : null);
    control?.updateValueAndValidity({ emitEvent: false });
  }

  parseHorario(horario: string): number {
    const [h, m] = horario.split(':').map(Number);
    return h * 60 + m;
  }

  formatarHorario(minutos: number): string {
    const h = Math.floor(minutos / 60);
    const m = minutos % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }

  private isAlmocoValido(
    inicioExpediente: string,
    fimExpediente: string,
    almocoInicio?: string,
    almocoFim?: string
  ): boolean {
    if (!almocoInicio && !almocoFim) {
      return true;
    }

    if (!almocoInicio || !almocoFim) {
      return false;
    }

    const inicioExpedienteMin = this.parseHorario(inicioExpediente);
    const fimExpedienteMin = this.parseHorario(fimExpediente);
    const almocoInicioMin = this.parseHorario(almocoInicio);
    const almocoFimMin = this.parseHorario(almocoFim);

    if (almocoFimMin <= almocoInicioMin) {
      return false;
    }

    return (
      almocoInicioMin >= inicioExpedienteMin && almocoFimMin <= fimExpedienteMin
    );
  }

  getDiasPresente(): IProfissionalDiasPresente[] {
    const diasSelecionados = this.form.get('diasSelecionados')?.value || [];
    const diasRetorno = diasSelecionados.map((dia: number | string) => ({
      diaId: Number(dia),
    }));

    return diasRetorno;
  }

  async adicionarProfissional() {
    if (this.$atingiuLimiteProfissionais()) {
      this.notificacaoService.notificacaoWarn(
        this.$mensagemLimiteProfissionais()
      );
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const almocoInicio = this.form.get('almocoInicio')?.value;
    const almocoFim = this.form.get('almocoFim')?.value;
    const inicioExpediente = this.form.get('inicioExpediente')?.value;
    const fimExpediente = this.form.get('fimExpediente')?.value;

    if (
      !this.isAlmocoValido(
        inicioExpediente,
        fimExpediente,
        almocoInicio,
        almocoFim
      )
    ) {
      this.notificacaoService.notificacaoWarn(
        this.translateService.instant('PRIMEIRO_ACESSO.PROFISSIONAIS.NOTIFICACAO_ALMOCO_INVALIDO')
      );
      return;
    }

    const estabelecimentoId = await this.obterEstabelecimentoId();
    if (!estabelecimentoId) {
      console.error('Estabelecimento não encontrado após recarregar dados');
      return;
    }

    const novoProfissional: Partial<IProfissional> = {
      nome: this.form.get('nome')?.value,
      descricao: this.form.get('descricao')?.value || undefined,
      perfilAcesso: this.form.get('perfilAcesso')?.value || undefined,
      prestaAtendimento: this.form.get('prestaAtendimento')?.value,
      ferias: false,
      inicioExpediente: this.form.get('inicioExpediente')?.value || undefined,
      fimExpediente: this.form.get('fimExpediente')?.value || undefined,
      almocoInicio: this.form.get('almocoInicio')?.value || undefined,
      almocoFim: this.form.get('almocoFim')?.value || undefined,
      estabelecimentoId,
      status: true,
      diasSemanaPresente: this.getDiasPresente(),
      servicos: (this.form.get('servicosSelecionados')?.value ?? []).map(
        (idOrObj: string | { id: string }) =>
          ({
            servicoId:
              typeof idOrObj === 'object' && idOrObj !== null
                ? idOrObj.id
                : String(idOrObj),
          } as unknown as IProfissionalServico)
      ),
    };

    this.profissionaisSignal.update((profissionais) => [
      ...profissionais,
      novoProfissional,
    ]);

    // Reset form mas mantém estabelecimento padrão
    this.form.reset({
      nome: '',
      descricao: '',
      perfilAcesso: '',
      prestaAtendimento: true,
      diasSelecionados: [],
      servicosSelecionados: [],
      inicioExpediente: '',
      fimExpediente: { value: '', disabled: true },
      almocoInicio: { value: '', disabled: true },
      almocoFim: { value: '', disabled: true },
    });
    this.horariosFim = [];
    this.horariosAlmocoInicio = [];
    this.horariosAlmocoFim = [];
  }

  removerProfissional(index: number) {
    this.profissionaisSignal.update((profissionais) =>
      profissionais.filter((_, i) => i !== index)
    );
  }

  nomeServico(servicoId: string): string {
    const servico = this.$servicos().find((s) => s.id === servicoId);
    return servico?.nome || '';
  }

  private async obterEstabelecimentoId(): Promise<string | undefined> {
    const idEmMemoria =
      this.estabelecimentoDataService.$estabelecimentoAtual()?.id ??
      this.estabelecimento?.id;

    if (idEmMemoria) {
      return idEmMemoria;
    }

    const estabelecimento = await this.estabelecimentoService.findCompleto();
    if (estabelecimento) {
      this.estabelecimento = estabelecimento;
      return estabelecimento.id;
    }

    return undefined;
  }

  async salvar() {
    const profissionais = this.profissionaisSignal();
    if (profissionais.length === 0) {
      this.notificacaoService.notificacaoWarn(
        this.translateService.instant('PRIMEIRO_ACESSO.PROFISSIONAIS.NOTIFICACAO_MINIMO')
      );
      return;
    }

    const estabelecimentoId = await this.obterEstabelecimentoId();

    try {
      this.$loading.set(true);

      if (profissionais.length > 0) {
        if (estabelecimentoId) {
          await this.profissionalService.criarMultiplo(
            profissionais.map((profissional) => ({
              ...profissional,
              estabelecimentoId,
            }))
          );
        } else {
          console.error(
            'Estabelecimento não encontrado após recarregar dados'
          );
        }
      }

      this.profissionaisSignal.set([]);
      this.proximaEtapa.emit();
    } catch (error) {
      console.error('Erro ao salvar profissionais:', error);
      this.notificacaoService.notificacaoError(
        this.translateService.instant('PRIMEIRO_ACESSO.PROFISSIONAIS.NOTIFICACAO_ERRO')
      );
    } finally {
      this.$loading.set(false);
    }
  }

  voltar() {
    this.etapaAnterior.emit();
  }
}
